import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Representation from "./Representation";
import { approveOauthRequest, fetchOauthConsentInfo } from "util/mcpApi";
import { OAUTH_CONSENT_PARAMS } from "util/mcpSetup";

/** Append OAuth response params to the client's redirect URI (already validated by the API). */
const withQuery = (uri, params) => {
  const url = new URL(uri);
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
};

/**
 * /connect/consent: the OAuth consent step for MCP clients such as Claude.
 * The API's /oauth/authorize sends the browser here; the signed-in user approves
 * and goes back to the client with an authorization code.
 */
const ConnectConsent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return OAUTH_CONSENT_PARAMS.reduce((acc, key) => {
      const value = query.get(key);
      if (value) acc[key] = value;
      return acc;
    }, {});
  }, [location.search]);

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    fetchOauthConsentInfo(params).then((res) => {
      if (!active) return;
      if (!res || res.cancel) {
        setError(t("mcp_connector.consent_failed"));
      } else if (res.error) {
        setError(res.error_description || t("mcp_connector.consent_invalid"));
      } else {
        setInfo(res);
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [params, t]);

  const handleApprove = async () => {
    setSubmitting(true);
    const res = await approveOauthRequest(params);
    if (res && res.redirect_url) {
      window.location.assign(res.redirect_url);
      return;
    }
    setSubmitting(false);
    setError(res?.message || res?.error_description || t("mcp_connector.consent_failed"));
  };

  const handleDeny = () => {
    window.location.assign(
      withQuery(info.redirect_uri, { error: "access_denied", state: params.state })
    );
  };

  return (
    <Representation
      t={t}
      loading={loading}
      error={error}
      info={info}
      submitting={submitting}
      onApprove={handleApprove}
      onDeny={handleDeny}
    />
  );
};

export default ConnectConsent;
